import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import MatchingScore from '../../../../common/src/components/MatchingScore';
import { Box, Grid } from '@wegroup/design-system';

// Components
import LabelValueWithSkeleton from './LabelValueWithSkeleton';

// Types
import {
  isOfferFailed,
  isOfferSuccessfull,
  LouiseOffer,
} from '../../../../../types/Quotes';
import { useParams } from 'react-router';
import { mq } from '../../../../common/src/utils/styledComponentsUtils';
import InsuranceCompanyLogo from '../../../../common/src/components/InsuranceCompanyLogo';
import {
  getEuroValue,
  getFieldValue,
  getGuaranteeNameForKey,
} from '../../services/productFactoryService';
import { showFranchise } from '../../../../tarifications/src/legacy/services/proFlowService';
import { useLouiseSelector } from '../../store/redux/store';

interface Props {
  offer: LouiseOffer;
}

/**
 * THIS VIEW IS BEING USED ON DESKTOP AND MOBILE
 */
const OfferGeneralInfo: React.FC<React.PropsWithChildren<Props>> = ({
  offer,
}) => {
  const { t } = useTranslation();
  const { riskObject }: { riskObject: string } = useParams();
  const shownProductsAmount = useLouiseSelector(
    (state) => state.offers.shownProductIds[offer.info.insurance_type]?.length,
  );

  const { state, info, details, premium, guarantee_content, matching_score } =
    offer;

  return (
    <Grid
      pt="6"
      pb="4"
      mx={{ base: 0, laptop: '8' }}
      borderBottom={{ base: 0, laptop: '1px' }}
      borderColor={{ base: 'unset', laptop: 'gray.50' }}
      gap={3}
      sx={{
        [mq.laptop]: {
          gridTemplateColumns: 'repeat(2,1fr)',
        },
        [mq.custom(1200)]: {
          gridTemplateColumns: 'repeat(3,1fr)',
        },
        [mq.laptopL]: {
          gridTemplateColumns:
            shownProductsAmount > 1 ? 'repeat(5,1fr)' : 'repeat(4,1fr)',
        },
        gridTemplateColumns: 'repeat(2,1fr)',
      }}
    >
      <Box
        display={{ base: 'none', laptop: 'block' }}
        mb="2"
        sx={{
          '> img': {
            objectFit: 'contain',
            width: '100%',
            maxHeight: '5rem',
          },
          [mq.laptop]: {
            gridColumn: 'span 2',
          },
          [mq.custom(1200)]: {
            '> img': {
              objectFit: 'contain',
              width: '12rem',
              maxHeight: '4rem',
            },
            gridColumn: 'unset',
          },
        }}
        gridRow={{ laptop: 'span 2', laptopL: 'unset' }}
      >
        <InsuranceCompanyLogo insuranceCompany={info.company.key} />
      </Box>
      <LabelValueWithSkeletonFirst label={info.company?.name || t('Insurer')}>
        {getGuaranteeNameForKey(info.insurance_name, guarantee_content)}
      </LabelValueWithSkeletonFirst>
      {{
        home: (
          <>
            <LabelValueWithSkeleton
              label={t(
                'SALES-CONVERSATION_OFFERS_SINGLE-OFFER_INFO_HOME_PROPERTY-VALUE',
              )}
            >
              {getEuroValue(state, premium?.insured_value?.value)}
            </LabelValueWithSkeleton>
            <LabelValueWithSkeleton
              label={t(
                'SALES-CONVERSATION_OFFERS_SINGLE-OFFER_INFO_HOME_CONTENT-VALUE',
              )}
            >
              {getEuroValue(state, premium?.insured_content_value?.value)}
            </LabelValueWithSkeleton>
          </>
        ),
        car: (
          <>
            <LabelValueWithSkeleton label={t('Bonus malus')}>
              {getFieldValue(state, details?.bonus_malus)}
            </LabelValueWithSkeleton>
            <LabelValueWithSkeleton label={t('Franchise')}>
              {getFieldValue(
                state,
                showFranchise(
                  premium?.franchise?.type,
                  premium?.franchise?.value,
                ),
              )}
            </LabelValueWithSkeleton>
          </>
        ),
      }[riskObject] || ''}
      {riskObject !== 'two_wheeler' && shownProductsAmount > 1 && (
        <LabelValueWithSkeleton label={t('Matching score')}>
          {/* Only works by explicitly passing the null */}
          {isOfferFailed(offer) || matching_score == null ? (
            '-'
          ) : isOfferSuccessfull(offer) ? (
            <MatchingScore score={matching_score} />
          ) : null}
        </LabelValueWithSkeleton>
      )}
    </Grid>
  );
};

const LabelValueWithSkeletonFirst = styled(LabelValueWithSkeleton)`
  display: none;
  ${mq.laptop} {
    display: block;
  }
`;

export default OfferGeneralInfo;
